import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Check, X, Trash2 } from 'lucide-react';
import { AVATARS, avatarById } from '../lib/avatars';

const NETFLIX_LOGO = 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg';
const STORAGE_KEY = 'nf_profiles';
const MAX_PROFILES = 5;

const DEFAULT_PROFILES = [
  { id: 'p1', name: 'Alex', avatarId: 'smiley' },
  { id: 'p2', name: 'Sam', avatarId: 'cool' },
  { id: 'p3', name: 'Danna', avatarId: 'star' },
  { id: 'p4', name: 'Kids', avatarId: 'panda' },
];

export default function Login() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [manage, setManage] = useState(false);
  const [editor, setEditor] = useState(null); // { mode, profile }

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      setProfiles(saved && saved.length ? saved : DEFAULT_PROFILES);
    } catch (e) {
      setProfiles(DEFAULT_PROFILES);
    }
  }, []);

  const persist = (list) => {
    setProfiles(list);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  };

  const chooseProfile = (p) => {
    if (manage) {
      setEditor({ mode: 'edit', profile: { ...p } });
      return;
    }
    localStorage.setItem('nf_active_profile', JSON.stringify(p));
    navigate('/browse');
  };

  const openAdd = () =>
    setEditor({
      mode: 'add',
      profile: { id: `p${Date.now()}`, name: '', avatarId: AVATARS[Math.floor(Math.random() * AVATARS.length)].id },
    });

  const saveEditor = () => {
    const p = { ...editor.profile, name: editor.profile.name.trim() || 'Guest' };
    if (editor.mode === 'add') {
      persist([...profiles, p]);
    } else {
      persist(profiles.map((x) => (x.id === p.id ? p : x)));
    }
    setEditor(null);
  };

  const deleteProfile = () => {
    persist(profiles.filter((x) => x.id !== editor.profile.id));
    setEditor(null);
  };

  return (
    <div className="min-h-screen bg-[#141414] flex flex-col">
      <header className="px-6 md:px-12 py-4 border-b border-white/10">
        <img src={NETFLIX_LOGO} alt="Netflix" className="h-7 md:h-9" />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <h1 className="text-3xl md:text-5xl text-white font-medium mb-8 md:mb-12">
          {manage ? 'Manage Profiles' : "Who's watching?"}
        </h1>

        <div className="flex flex-wrap items-start justify-center gap-4 md:gap-8">
          {profiles.map((p) => (
            <button
              key={p.id}
              onClick={() => chooseProfile(p)}
              className="group flex flex-col items-center gap-3"
            >
              <div className="relative w-24 h-24 md:w-36 md:h-36 rounded-md overflow-hidden border-2 border-transparent group-hover:border-white transition-all">
                <img src={avatarById(p.avatarId)} alt={p.name} className="w-full h-full object-cover" />
                {manage && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Pencil size={28} className="text-white" />
                  </div>
                )}
              </div>
              <span className="text-[#808080] group-hover:text-white text-lg md:text-xl transition-colors">
                {p.name}
              </span>
            </button>
          ))}

          {!manage && profiles.length < MAX_PROFILES && (
            <button onClick={openAdd} className="group flex flex-col items-center gap-3">
              <div className="w-24 h-24 md:w-36 md:h-36 rounded-md flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#2a2a2a] flex items-center justify-center text-[#808080] group-hover:bg-[#333] group-hover:text-white transition-colors">
                  <Plus size={40} />
                </div>
              </div>
              <span className="text-[#808080] group-hover:text-white text-lg md:text-xl transition-colors">
                Add Profile
              </span>
            </button>
          )}
        </div>

        <button
          onClick={() => setManage((m) => !m)}
          className={`mt-12 md:mt-16 border px-6 py-2 tracking-widest transition-colors ${
            manage
              ? 'bg-white text-black border-white'
              : 'border-[#808080] text-[#808080] hover:text-white hover:border-white'
          }`}
        >
          {manage ? 'DONE' : 'MANAGE PROFILES'}
        </button>
      </div>

      {editor && (
        <AvatarEditor
          editor={editor}
          setEditor={setEditor}
          onSave={saveEditor}
          onDelete={deleteProfile}
        />
      )}
    </div>
  );
}

function AvatarEditor({ editor, setEditor, onSave, onDelete }) {
  const { mode, profile } = editor;
  const update = (patch) => setEditor({ ...editor, profile: { ...profile, ...patch } });

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 animate-fade-in">
      <div className="w-full max-w-lg bg-[#181818] rounded-lg overflow-hidden border border-white/10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">
            {mode === 'add' ? 'Add Profile' : 'Edit Profile'}
          </h2>
          <button onClick={() => setEditor(null)} className="text-white/60 hover:text-white">
            <X size={22} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <img
              src={avatarById(profile.avatarId)}
              alt="preview"
              className="w-20 h-20 rounded-md flex-shrink-0"
            />
            <input
              autoFocus
              value={profile.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Enter name"
              maxLength={20}
              className="flex-1 bg-[#2a2a2a] text-white text-lg px-4 py-2.5 rounded outline-none border border-transparent focus:border-white/40"
            />
          </div>

          <p className="text-sm text-white/60 mb-3">Choose a character</p>
          <div className="grid grid-cols-5 gap-3">
            {AVATARS.map((a) => {
              const selected = a.id === profile.avatarId;
              return (
                <button
                  key={a.id}
                  onClick={() => update({ avatarId: a.id })}
                  title={a.name}
                  className={`relative rounded-md overflow-hidden aspect-square transition-transform hover:scale-105 ${
                    selected ? 'ring-2 ring-white' : 'ring-1 ring-white/10'
                  }`}
                >
                  <img src={a.src} alt={a.name} className="w-full h-full object-cover" />
                  {selected && (
                    <span className="absolute top-1 right-1 bg-[#e50914] rounded-full p-0.5">
                      <Check size={12} className="text-white" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
          {mode === 'edit' ? (
            <button
              onClick={onDelete}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-[#e50914] transition-colors"
            >
              <Trash2 size={16} /> Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setEditor(null)}
              className="px-5 py-2 text-sm text-white/70 hover:text-white border border-white/30 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-6 py-2 text-sm font-semibold bg-[#e50914] text-white rounded hover:bg-[#f6121d] transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
